import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchField, type SearchFieldProps } from '@attendance/ui';

/* ===========================================================================
 * THE APP'S SEARCH BOX — `SearchField` with the words already in it.
 *
 * Thirty screens draw a search box and they were already one control, which is
 * the good half of the story. The bad half was the copy: `clearLabel` is the
 * accessible name of the × and it is the only thing that control ever says, and
 * TWO of the thirty passed one. The other twenty-eight took the primitive's
 * hard-coded English 'Clear' — in a product whose second locale is Arabic, on
 * the control a screen-reader user needs most, because a × has no other name.
 *
 * `packages/ui` cannot reach i18n and should not: a primitive that ships copy
 * ships one language. So the default lives here, once, and the primitive now
 * REQUIRES the prop — which is what turns "twenty-eight screens forgot" into a
 * compile error nobody can forget.
 *
 * THE PLACEHOLDER IS DERIVED, not required. Half the boxes passed one and half
 * did not, so half showed "Search branches…" and half showed an empty field
 * under a label a sighted reader cannot see. Giving `label` and getting a
 * placeholder from it means the two can no longer disagree; a screen that wants
 * different words still passes its own.
 * ======================================================================== */

export const SearchBox = forwardRef<
  HTMLInputElement,
  Omit<SearchFieldProps, 'clearLabel'> & { clearLabel?: string }
>(function SearchBox({ clearLabel, placeholder, label, ...rest }, ref) {
  const { t } = useTranslation();
  return (
    <SearchField
      ref={ref}
      label={label}
      placeholder={placeholder ?? t('common.searchPlaceholder', '{{what}}…', { what: label })}
      clearLabel={clearLabel ?? t('common.clearSearch', 'Clear the search')}
      {...rest}
    />
  );
});
